// Pet plaque designer — the shared design model.
//
// A plaque design is small enough to live entirely in a URL, so there is no
// DB storage: the owner designs at /plaque, the address bar *is* the design,
// and staff open that same link to download the production SVG from
// /api/plaque/svg. Hucklevet and the catalog link to /plaque (optionally
// prefilled with ?n=<pet name>).
//
// The default look follows Larsen's Art 'n' Sign production proofs: a
// 106 × 60 mm brushed-gold plate with notched corners and an inset border,
// the name in Trajan-style capitals, a ruled divider with a paw, a spaced-caps
// subtitle, an italic phrase over up to two lines, and the dates.
//
// Everything here is pure so the live preview (browser) and the production
// file (server, outlined with opentype.js) run the exact same layout. Both
// pass in a `measure` function for real text widths; the built-in estimator
// is only a fallback before fonts have loaded.

/** Physical plate, in millimetres. One constant so a supplier change is a one-line edit. */
export const PLAQUE_SPEC = {
  widthMm: 106,
  heightMm: 60,
  /** Radius of the concave quarter-circle notch cut from each corner. */
  notchRadiusMm: 5,
  /** Engraved border: distance in from the plate edge, and line thickness. */
  borderInsetMm: 2.4,
  borderWidthMm: 0.7,
  /** Text keeps this far inside the plate edge. */
  marginXMm: 9,
  marginYMm: 5.5,
} as const

export const PLAQUE_LIMITS = {
  name: 20,
  subtitle: 30,
  phrase: 90,
} as const

export type PlaqueFontPreset = "classic" | "script" | "modern"
/** Ornament on the divider under the name; "none" drops the divider entirely. */
export type PlaqueIcon = "paw" | "heart" | "line" | "none"
export type PlaqueDateStyle = "years" | "full"

export interface PlaqueDesign {
  name: string
  /** Short line in spaced capitals under the divider, e.g. "A true gentleman". */
  subtitle: string
  /** Italic phrase; wraps onto two lines when long. */
  phrase: string
  /** "YYYY" or "YYYY-MM-DD" */
  dateFrom: string
  /** "YYYY" or "YYYY-MM-DD" */
  dateTo: string
  dateStyle: PlaqueDateStyle
  font: PlaqueFontPreset
  icon: PlaqueIcon
}

export const DEFAULT_PLAQUE_DESIGN: PlaqueDesign = {
  name: "",
  subtitle: "",
  phrase: "",
  dateFrom: "",
  dateTo: "",
  dateStyle: "years",
  font: "classic",
  icon: "paw",
}

/** The font faces the presets draw from. */
export type PlaqueFontFace = "capitals" | "serifItalic" | "script" | "sans"

/**
 * Google Fonts query (css2 `family=` value) for each face. The server fetches
 * these as TTF for outlining; the browser loads the same faces via next/font.
 */
export const PLAQUE_FONT_FACES: Record<PlaqueFontFace, { family: string; query: string }> = {
  capitals: { family: "Cinzel", query: "Cinzel:wght@600" },
  serifItalic: { family: "Cormorant Garamond", query: "Cormorant+Garamond:ital,wght@1,600" },
  script: { family: "Great Vibes", query: "Great+Vibes" },
  sans: { family: "Montserrat", query: "Montserrat:wght@500" },
}

interface LineStyle {
  face: PlaqueFontFace
  /** Target font size (em) in mm, before shrink-to-fit. */
  sizeMm: number
  /** Extra spacing between letters, as a fraction of the font size. */
  tracking: number
  uppercase: boolean
}

type PresetLines = Record<"name" | "subtitle" | "phrase" | "dates", LineStyle>

export const PLAQUE_FONT_PRESETS: Record<PlaqueFontPreset, { label: string } & PresetLines> = {
  classic: {
    label: "Classic",
    name: { face: "capitals", sizeMm: 12.5, tracking: 0.06, uppercase: true },
    subtitle: { face: "capitals", sizeMm: 4.4, tracking: 0.22, uppercase: true },
    phrase: { face: "serifItalic", sizeMm: 5.6, tracking: 0.01, uppercase: false },
    // Cinzel for its lining figures — Cormorant's old-style numerals dip below the line.
    dates: { face: "capitals", sizeMm: 4, tracking: 0.04, uppercase: false },
  },
  script: {
    label: "Script",
    name: { face: "script", sizeMm: 16, tracking: 0, uppercase: false },
    subtitle: { face: "capitals", sizeMm: 4.4, tracking: 0.22, uppercase: true },
    phrase: { face: "serifItalic", sizeMm: 5.6, tracking: 0.01, uppercase: false },
    // Cinzel for its lining figures — Cormorant's old-style numerals dip below the line.
    dates: { face: "capitals", sizeMm: 4, tracking: 0.04, uppercase: false },
  },
  modern: {
    label: "Modern",
    name: { face: "sans", sizeMm: 10, tracking: 0.14, uppercase: true },
    subtitle: { face: "sans", sizeMm: 3.6, tracking: 0.24, uppercase: true },
    phrase: { face: "sans", sizeMm: 4.2, tracking: 0.02, uppercase: false },
    dates: { face: "sans", sizeMm: 3.6, tracking: 0.08, uppercase: false },
  },
}

export const PLAQUE_SUBTITLE_SUGGESTIONS = ["A true gentleman", "A true lady", "Best friend", "Much loved"]

export const PLAQUE_PHRASE_SUGGESTIONS = [
  "Forever loved",
  "Always by our side",
  "Forever loyal, forever loved, forever in our hearts.",
]

const FONT_PRESETS: PlaqueFontPreset[] = ["classic", "script", "modern"]
const ICONS: PlaqueIcon[] = ["paw", "heart", "line", "none"]
const DATE_STYLES: PlaqueDateStyle[] = ["years", "full"]

// ---------------------------------------------------------------------------
// Sanitising + URL encoding
// ---------------------------------------------------------------------------

// Control chars, zero-width/bidi marks and line/paragraph separators. Built
// from an escaped string so no raw U+2028 ever sits inside a regex literal.
const INVISIBLE_CHARS = new RegExp("[\\u0000-\\u001f\\u007f-\\u009f\\u200b-\\u200f\\u2028-\\u202e\\ufeff]", "g")

/** Strip control characters, collapse whitespace, trim, and clamp length. */
export function cleanPlaqueText(value: unknown, max: number): string {
  if (typeof value !== "string") return ""
  return Array.from(
    value
      .replace(INVISIBLE_CHARS, " ")
      .replace(/[<>]/g, "")
      .replace(/\s+/g, " ")
      .trim()
  )
    .slice(0, max)
    .join("")
    .trim()
}

/** Accepts "YYYY" or "YYYY-MM-DD" (a real calendar date); anything else → "". */
export function cleanPlaqueDate(value: unknown): string {
  if (typeof value !== "string") return ""
  const v = value.trim()
  if (/^\d{4}$/.test(v)) {
    const y = Number(v)
    return y >= 1900 && y <= 2100 ? v : ""
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v)
  if (!m) return ""
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])]
  if (y < 1900 || y > 2100) return ""
  const date = new Date(Date.UTC(y, mo - 1, d))
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== mo - 1 || date.getUTCDate() !== d) return ""
  return v
}

function pick<T extends string>(value: unknown, allowed: T[], fallback: T): T {
  return typeof value === "string" && (allowed as string[]).includes(value) ? (value as T) : fallback
}

export function normalizePlaqueDesign(input: Partial<Record<keyof PlaqueDesign, unknown>>): PlaqueDesign {
  return {
    name: cleanPlaqueText(input.name, PLAQUE_LIMITS.name),
    subtitle: cleanPlaqueText(input.subtitle, PLAQUE_LIMITS.subtitle),
    phrase: cleanPlaqueText(input.phrase, PLAQUE_LIMITS.phrase),
    dateFrom: cleanPlaqueDate(input.dateFrom),
    dateTo: cleanPlaqueDate(input.dateTo),
    dateStyle: pick(input.dateStyle, DATE_STYLES, DEFAULT_PLAQUE_DESIGN.dateStyle),
    font: pick(input.font, FONT_PRESETS, DEFAULT_PLAQUE_DESIGN.font),
    icon: pick(input.icon, ICONS, DEFAULT_PLAQUE_DESIGN.icon),
  }
}

// Short query keys keep shared links tidy.
const PARAM_KEYS: Record<keyof PlaqueDesign, string> = {
  name: "n",
  subtitle: "s",
  phrase: "p",
  dateFrom: "f",
  dateTo: "t",
  dateStyle: "ds",
  font: "font",
  icon: "icon",
}

/** Query string for a design; defaults are omitted so links stay short. */
export function encodePlaqueParams(design: PlaqueDesign): string {
  const params = new URLSearchParams()
  for (const key of Object.keys(PARAM_KEYS) as Array<keyof PlaqueDesign>) {
    const value = design[key]
    if (value && value !== DEFAULT_PLAQUE_DESIGN[key]) params.set(PARAM_KEYS[key], value)
  }
  return params.toString()
}

type SearchParamsLike =
  | URLSearchParams
  | { get(name: string): string | null }
  | Record<string, string | string[] | undefined>

export function decodePlaqueParams(searchParams: SearchParamsLike | null | undefined): PlaqueDesign {
  if (!searchParams) return { ...DEFAULT_PLAQUE_DESIGN }
  const get = (k: string): string | undefined => {
    if (typeof (searchParams as URLSearchParams).get === "function") {
      return (searchParams as URLSearchParams).get(k) ?? undefined
    }
    const v = (searchParams as Record<string, string | string[] | undefined>)[k]
    return Array.isArray(v) ? v[0] : v
  }
  const raw: Partial<Record<keyof PlaqueDesign, unknown>> = {}
  for (const key of Object.keys(PARAM_KEYS) as Array<keyof PlaqueDesign>) {
    raw[key] = get(PARAM_KEYS[key])
  }
  return normalizePlaqueDesign(raw)
}

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

function formatOneDate(value: string, style: PlaqueDateStyle): string {
  if (!value) return ""
  if (style === "years" || value.length === 4) return value.slice(0, 4)
  const [y, m, d] = value.split("-").map(Number)
  return `${d} ${MONTHS[m - 1]} ${y}`
}

/** "2009 – 2024", "12 Mar 2009 – 4 Jun 2024", or just one side when only one is set. */
export function formatPlaqueDates(design: Pick<PlaqueDesign, "dateFrom" | "dateTo" | "dateStyle">): string {
  const from = formatOneDate(design.dateFrom, design.dateStyle)
  const to = formatOneDate(design.dateTo, design.dateStyle)
  if (from && to) return `${from} – ${to}`
  return from || to
}

// ---------------------------------------------------------------------------
// Icons (24×24 path data, filled)
// ---------------------------------------------------------------------------

function circlePath(cx: number, cy: number, r: number): string {
  return ellipsePath(cx, cy, r, r)
}
function ellipsePath(cx: number, cy: number, rx: number, ry: number): string {
  return `M${cx - rx} ${cy}a${rx} ${ry} 0 1 0 ${2 * rx} 0a${rx} ${ry} 0 1 0 ${-2 * rx} 0Z`
}

export const PLAQUE_ICON_PATHS: Record<"paw" | "heart", string> = {
  paw: [
    ellipsePath(12, 16.2, 5.2, 4.4),
    circlePath(5.2, 10.4, 2.2),
    circlePath(9.2, 6.4, 2.3),
    circlePath(14.8, 6.4, 2.3),
    circlePath(18.8, 10.4, 2.2),
  ].join(""),
  heart:
    "M12 21.2C5.6 16.1 2 12.8 2 8.6 2 5.5 4.4 3 7.4 3c1.8 0 3.5.9 4.6 2.4C13.1 3.9 14.8 3 16.6 3 19.6 3 22 5.5 22 8.6c0 4.2-3.6 7.5-10 12.6Z",
}

// ---------------------------------------------------------------------------
// Plate outline
// ---------------------------------------------------------------------------

/**
 * The notched-corner rectangle, offset `inset` mm inward from the plate edge.
 * The notches are quarter circles centred on the plate corners, so an inset
 * outline keeps the same centres with radius notch + inset.
 */
export function plaqueOutlinePath(inset = 0): string {
  const { widthMm: W, heightMm: H, notchRadiusMm: r } = PLAQUE_SPEC
  const R = r + inset
  const a = Math.sqrt(R * R - inset * inset) // where the arc meets the inset edge
  const n = (v: number) => +v.toFixed(3)
  const lo = n(inset)
  const hiX = n(W - inset)
  const hiY = n(H - inset)
  const R3 = n(R)
  return [
    `M${n(a)} ${lo}`,
    `H${n(W - a)}`,
    `A${R3} ${R3} 0 0 0 ${hiX} ${n(a)}`,
    `V${n(H - a)}`,
    `A${R3} ${R3} 0 0 0 ${n(W - a)} ${hiY}`,
    `H${n(a)}`,
    `A${R3} ${R3} 0 0 0 ${lo} ${n(H - a)}`,
    `V${n(a)}`,
    `A${R3} ${R3} 0 0 0 ${n(a)} ${lo}`,
    "Z",
  ].join("")
}

/** The engraved border as a filled ring (draw with fill-rule="evenodd"). */
export function plaqueBorderPath(): string {
  const { borderInsetMm: i, borderWidthMm: w } = PLAQUE_SPEC
  return plaqueOutlinePath(i - w / 2) + plaqueOutlinePath(i + w / 2)
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

/** Width in mm of `text` set in `face` at `sizeMm`, excluding tracking. */
export type PlaqueMeasure = (text: string, face: PlaqueFontFace, sizeMm: number) => number

// Rough average advance widths (fraction of em) — only used until real fonts
// are measured.
const ESTIMATE_ADVANCE: Record<PlaqueFontFace, { lower: number; upper: number; other: number }> = {
  capitals: { lower: 0.62, upper: 0.74, other: 0.55 },
  serifItalic: { lower: 0.4, upper: 0.6, other: 0.42 },
  script: { lower: 0.38, upper: 0.7, other: 0.42 },
  sans: { lower: 0.56, upper: 0.72, other: 0.58 },
}

export const estimatePlaqueTextWidth: PlaqueMeasure = (text, face, sizeMm) => {
  const a = ESTIMATE_ADVANCE[face]
  let em = 0
  for (const ch of text) {
    if (ch === " ") em += 0.25
    else if (ch >= "a" && ch <= "z") em += a.lower
    else if (ch >= "A" && ch <= "Z") em += a.upper
    else em += a.other
  }
  return em * sizeMm
}

export type PlaqueLineRole = "name" | "subtitle" | "phrase" | "dates"

export interface PlaqueLine {
  role: PlaqueLineRole
  text: string
  face: PlaqueFontFace
  sizeMm: number
  /** Letter spacing in mm. */
  trackingMm: number
  /** Total advance width including tracking between letters. */
  widthMm: number
  /** Left edge so the line is centred. */
  x: number
  baseline: number
}

export interface PlaqueIconPlacement {
  icon: "paw" | "heart"
  /** Top-left of the icon box, in mm. */
  x: number
  y: number
  sizeMm: number
}

/** A filled rectangle — the divider rules. */
export interface PlaqueRule {
  x: number
  y: number
  width: number
  height: number
}

export interface PlaqueLayout {
  lines: PlaqueLine[]
  icons: PlaqueIconPlacement[]
  rules: PlaqueRule[]
}

// Visual heights as a fraction of font size, used to stack the lines.
const CAP_HEIGHT: Record<PlaqueFontFace, number> = { capitals: 0.7, serifItalic: 0.63, script: 0.72, sans: 0.7 }
const DESCENT: Record<PlaqueFontFace, number> = { capitals: 0.04, serifItalic: 0.2, script: 0.25, sans: 0.2 }
// Script capitals swash well past their advance width, so fit them narrower.
const FIT_WIDTH: Partial<Record<PlaqueFontFace, number>> = { script: 0.9 }

const GAP_MM = 2.6
const PHRASE_LEADING = 1.22
/** Wrap the phrase once a single line would fill more than this share of the width. */
const PHRASE_WRAP_AT = 0.82
const DIVIDER = { widthShare: 0.62, iconMm: 6.4, ruleMm: 0.4, gapMm: 1.6 }

function lineWidth(text: string, face: PlaqueFontFace, sizeMm: number, tracking: number, measure: PlaqueMeasure) {
  const letters = Array.from(text).length
  return measure(text, face, sizeMm) + Math.max(0, letters - 1) * tracking * sizeMm
}

/**
 * Split into two lines at the word break that makes the longer line shortest,
 * preferring a break after punctuation ("forever loved, / forever in…") when
 * it's nearly as balanced.
 */
function balanceTwoLines(text: string, width: (s: string) => number): [string, string] | null {
  const words = text.split(" ")
  if (words.length < 2) return null
  let best: [string, string] | null = null
  let bestMax = Infinity
  for (let i = 1; i < words.length; i++) {
    const a = words.slice(0, i).join(" ")
    const b = words.slice(i).join(" ")
    const m = Math.max(width(a), width(b)) * (/[,;:.!?]$/.test(a) ? 0.88 : 1)
    if (m < bestMax) {
      bestMax = m
      best = [a, b]
    }
  }
  return best
}

type Block =
  | { kind: "text"; role: PlaqueLineRole; text: string; style: LineStyle; sizeMm: number; leadingBefore: number }
  | { kind: "divider"; heightMm: number }

/**
 * Positions every element on the plate: name, divider (rules + ornament),
 * subtitle, phrase (one or two lines) and dates. Lines shrink to fit the safe
 * width; if the whole stack is still too tall, everything scales together.
 */
export function layoutPlaque(design: PlaqueDesign, measure: PlaqueMeasure = estimatePlaqueTextWidth): PlaqueLayout {
  const preset = PLAQUE_FONT_PRESETS[design.font]
  const { widthMm: W, heightMm: H, marginXMm, marginYMm } = PLAQUE_SPEC
  const maxWidth = W - 2 * marginXMm
  const maxHeight = H - 2 * marginYMm

  const fitText = (role: PlaqueLineRole, raw: string, style: LineStyle, leadingBefore = 0): Block => {
    const text = style.uppercase ? raw.toLocaleUpperCase() : raw
    const natural = lineWidth(text, style.face, style.sizeMm, style.tracking, measure)
    const available = maxWidth * (FIT_WIDTH[style.face] ?? 1)
    const fit = natural > 0 ? Math.min(1, available / natural) : 1
    return { kind: "text", role, text, style, sizeMm: style.sizeMm * fit, leadingBefore }
  }

  const blocks: Block[] = []
  if (design.name) blocks.push(fitText("name", design.name, preset.name))
  if (design.icon !== "none" && design.name) blocks.push({ kind: "divider", heightMm: DIVIDER.iconMm })
  if (design.subtitle) blocks.push(fitText("subtitle", design.subtitle, preset.subtitle))
  if (design.phrase) {
    const s = preset.phrase
    const w = (t: string) => lineWidth(t, s.face, s.sizeMm, s.tracking, measure)
    const split = w(design.phrase) > maxWidth * PHRASE_WRAP_AT ? balanceTwoLines(design.phrase, w) : null
    if (split) {
      const [a, b] = [fitText("phrase", split[0], s), fitText("phrase", split[1], s)]
      // Both lines share the smaller size so the pair reads as one phrase.
      const size = Math.min((a as { sizeMm: number }).sizeMm, (b as { sizeMm: number }).sizeMm)
      blocks.push({ ...(a as Extract<Block, { kind: "text" }>), sizeMm: size })
      blocks.push({ ...(b as Extract<Block, { kind: "text" }>), sizeMm: size, leadingBefore: size * PHRASE_LEADING })
    } else {
      blocks.push(fitText("phrase", design.phrase, s))
    }
  }
  const dates = formatPlaqueDates(design)
  if (dates) blocks.push(fitText("dates", dates, preset.dates))

  const textAscent = (b: Extract<Block, { kind: "text" }>) => b.sizeMm * CAP_HEIGHT[b.style.face]
  const textDescent = (b: Extract<Block, { kind: "text" }>) =>
    b.sizeMm * DESCENT[b.style.face]

  // Total height at a given scale. A wrapped phrase's second line advances by
  // its leading (baseline to baseline) instead of the usual gap.
  const measureStack = (scale: number) => {
    let h = 0
    blocks.forEach((b, i) => {
      if (b.kind === "divider") {
        h += (i > 0 ? GAP_MM * scale : 0) + b.heightMm * scale
        return
      }
      if (b.leadingBefore) {
        const prev = blocks[i - 1] as Extract<Block, { kind: "text" }>
        h += b.leadingBefore * scale - textDescent(prev) * scale + textDescent(b) * scale
        return
      }
      h += (i > 0 ? GAP_MM * scale : 0) + (textAscent(b) + textDescent(b)) * scale
    })
    return h
  }
  const natural = measureStack(1)
  const scale = natural > maxHeight ? maxHeight / natural : 1

  const lines: PlaqueLine[] = []
  const icons: PlaqueIconPlacement[] = []
  const rules: PlaqueRule[] = []
  let cursor = (H - measureStack(scale)) / 2
  let lastBaseline = 0

  blocks.forEach((b, i) => {
    if (b.kind === "divider") {
      if (i > 0) cursor += GAP_MM * scale
      const mid = cursor + (b.heightMm * scale) / 2
      const total = W * DIVIDER.widthShare
      const ruleH = DIVIDER.ruleMm * scale
      if (design.icon === "line") {
        rules.push({ x: (W - total) / 2, y: mid - ruleH / 2, width: total, height: ruleH })
      } else {
        const iconSize = DIVIDER.iconMm * scale
        const gap = DIVIDER.gapMm * scale
        const side = (total - iconSize - 2 * gap) / 2
        icons.push({ icon: design.icon as "paw" | "heart", x: (W - iconSize) / 2, y: mid - iconSize / 2, sizeMm: iconSize })
        rules.push({ x: (W - total) / 2, y: mid - ruleH / 2, width: side, height: ruleH })
        rules.push({ x: (W + total) / 2 - side, y: mid - ruleH / 2, width: side, height: ruleH })
      }
      cursor += b.heightMm * scale
      return
    }
    const sizeMm = b.sizeMm * scale
    let baseline: number
    if (b.leadingBefore) {
      baseline = lastBaseline + b.leadingBefore * scale
    } else {
      if (i > 0) cursor += GAP_MM * scale
      baseline = cursor + sizeMm * CAP_HEIGHT[b.style.face]
    }
    cursor = baseline + textDescent(b) * scale
    lastBaseline = baseline
    const widthMm = lineWidth(b.text, b.style.face, sizeMm, b.style.tracking, measure)
    lines.push({
      role: b.role,
      text: b.text,
      face: b.style.face,
      sizeMm,
      trackingMm: b.style.tracking * sizeMm,
      widthMm,
      x: (W - widthMm) / 2,
      baseline,
    })
  })

  return { lines, icons, rules }
}
/** Filename-safe slug for downloads. */
export function plaqueFileSlug(name: string): string {
  const slug = name
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
  return slug || "pet"
}
